import React, { useState, useMemo } from "react";

const PER_THOUSAND_PRICE = 1.2; // USD per 1,000 extra active master images
const MINIMUM_CHARGE = 5.0; // USD (includes a base amount of usage)
const BASE_INCLUDED_IMAGES = 4200; // images included in the minimum charge
const BASE_FREE_BW_GB = 5; // GB
const FREE_BW_PER_1000 = 1.2; // GB per 1,000 master images
const OVERAGE_PRICE_PER_GB = 0.1; // USD per GB

const formatNumber = (value) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatMoney = (value) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatGB = (value) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const PricingCalculator = () => {
  const [masterImages, setMasterImages] = useState(4200);
  const [bandwidthGB, setBandwidthGB] = useState(5);

  const metrics = useMemo(() => {
    const images = Math.max(0, masterImages || 0);
    const bandwidth = Math.max(0, bandwidthGB || 0);

    const imageUnits = images === 0 ? 0 : Math.ceil(images / 1000);

    const extraImages = Math.max(0, images - BASE_INCLUDED_IMAGES);
    const extraUnits = extraImages === 0 ? 0 : Math.ceil(extraImages / 1000);
    const imageCost = MINIMUM_CHARGE + extraUnits * PER_THOUSAND_PRICE;

    const freeBandwidthGB = Math.max(
      BASE_FREE_BW_GB,
      (images / 1000) * FREE_BW_PER_1000
    );

    const overageGB = Math.max(0, bandwidth - freeBandwidthGB);
    const bandwidthCost = overageGB * OVERAGE_PRICE_PER_GB;

    const total = imageCost + bandwidthCost;

    return {
      imageUnits,
      imageCost,
      freeBandwidthGB,
      overageGB,
      bandwidthCost,
      total,
    };
  }, [masterImages, bandwidthGB]);

  const handleImagesChange = (value) => {
    const numeric = parseInt(value, 10);
    if (Number.isNaN(numeric)) return;
    setMasterImages(clamp(numeric, 0, 1000000));
  };

  const handleBandwidthChange = (value) => {
    const numeric = parseFloat(value);
    if (Number.isNaN(numeric)) return;
    setBandwidthGB(clamp(numeric, 0, 10000));
  };

  return (
    <div className="pricing-calculator">
      <div className="pricing-calculator-header">
        <h2>Estimate your monthly bill</h2>
        <p>
          Simulate how ImageBoss scales with your usage. Adjust your{" "}
          <strong>active master images</strong> and{" "}
          <strong>monthly data transfer</strong> to see an estimate.
        </p>
      </div>

      <div className="pricing-calculator-body">
        <div className="pricing-calculator-inputs">
          <div className="pricing-calculator-field">
            <label>
              <span className="pricing-calculator-label-with-tooltip">
                <span>Active master images</span>
                <span className="pricing-calculator-tooltip" aria-label="What is a master image?" role="tooltip">
                  <span className="pricing-calculator-tooltip-icon">?</span>
                  <span className="pricing-calculator-tooltip-content">
                    A master image (or origin image) is the original file you store and request from ImageBoss. You can create unlimited resized or transformed versions of the same master image; we only count the master itself for billing.
                  </span>
                </span>
              </span>
              <span className="pricing-calculator-label-hint">
                Only images actually requested are billed.
              </span>
            </label>
            <div className="pricing-calculator-control">
              <input
                type="range"
                min="0"
                max="200000"
                step="1000"
                value={clamp(masterImages, 0, 200000)}
                onChange={(e) => handleImagesChange(e.target.value)}
              />
              <input
                type="number"
                min="0"
                max="1000000"
                step="100"
                value={masterImages}
                onChange={(e) => handleImagesChange(e.target.value)}
              />
            </div>
            <div className="pricing-calculator-help">
              ≈ {metrics.imageUnits} block
              {metrics.imageUnits === 1 ? "" : "s"} of 1,000 active images at $
              {formatMoney(PER_THOUSAND_PRICE)} / 1,000.
            </div>
          </div>

          <div className="pricing-calculator-field">
            <label>
              Monthly data transfer (GB)
              <span className="pricing-calculator-label-hint">
                CDN bandwidth used to deliver your optimized images.
              </span>
            </label>
            <div className="pricing-calculator-control">
              <input
                type="range"
                min="0"
                max="500"
                step="1"
                value={clamp(bandwidthGB, 0, 500)}
                onChange={(e) => handleBandwidthChange(e.target.value)}
              />
              <input
                type="number"
                min="0"
                max="10000"
                step="1"
                value={bandwidthGB}
                onChange={(e) => handleBandwidthChange(e.target.value)}
              />
            </div>
            <div className="pricing-calculator-help">
              Includes <strong>{formatGB(metrics.freeBandwidthGB)} GB</strong>{" "}
              of elastic bandwidth. Overage at $
              {formatMoney(OVERAGE_PRICE_PER_GB)} / GB.
            </div>
          </div>
        </div>

        <div className="pricing-calculator-summary">
          <div className="pricing-calculator-total">
            <div className="label">Estimated monthly bill</div>
            <div className="value">
              <span className="currency">$</span>
              <span className="amount">{formatMoney(metrics.total)}</span>
            </div>
            <div className="disclaimer">
              This is an estimate based on current public pricing. Final
              billing depends on your actual usage.
            </div>
          </div>

          <div className="pricing-calculator-breakdown">
            <div className="row">
              <span>Images</span>
              <span>${formatMoney(metrics.imageCost)}</span>
            </div>
            <div className="row muted">
              <span>
                Minimum charge of ${formatMoney(MINIMUM_CHARGE)} includes ~
                4,200 active images and elastic bandwidth.
              </span>
            </div>
            <div className="row">
              <span>
                Bandwidth over{" "}
                {metrics.freeBandwidthGB > 0
                  ? `${formatGB(metrics.freeBandwidthGB)} GB`
                  : "included amount"}
              </span>
              <span>
                {metrics.overageGB <= 0
                  ? "Included"
                  : `$${formatMoney(metrics.bandwidthCost)}`}
              </span>
            </div>
            {metrics.overageGB > 0 && (
              <div className="row muted">
                <span>
                  {formatGB(metrics.overageGB)} GB × $
                  {formatMoney(OVERAGE_PRICE_PER_GB)} / GB
                </span>
              </div>
            )}
          </div>
          {metrics.total === MINIMUM_CHARGE &&
            masterImages <= BASE_INCLUDED_IMAGES &&
            bandwidthGB <= BASE_FREE_BW_GB && (
              <div className="pricing-calculator-topup">
                <strong>You’re within the included minimum usage.</strong>{" "}
                Up to {formatNumber(BASE_INCLUDED_IMAGES)} master images and{" "}
                {formatGB(BASE_FREE_BW_GB)} GB of bandwidth are covered by the $
                {formatMoney(MINIMUM_CHARGE)} minimum.
              </div>
            )}
        </div>
      </div>

      <div className="pricing-calculator-cta">
        <a
          href="/users/sign_up?plan=as-you-go&period=monthly"
          className="btn btn-primary"
        >
          Start your free trial
        </a>
      </div>
    </div>
  );
};

export default PricingCalculator;

